import { useState, useRef, useCallback } from "react";
import useHttp from "../../use-http/use-http";
import { parseCSVFile, parseExcelFile } from "../utils/fileParser";
import { validateBulkData, transformBulkData } from "../utils/dataProcessor";

// Shared hook for bulk upload functionality
export const useBulkUpload = (type, corpId, corpName) => {
  const fileInputRef = useRef();
  const [uploadState, setUploadState] = useState("idle"); // idle, parsing, preview, uploading, complete
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, errors: [] });
  const [uploadResults, setUploadResults] = useState({ success: 0, failed: 0, errors: [] });
  const [dragActive, setDragActive] = useState(false);

  const { isLoading, sendRequest } = useHttp();

  // File drag handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  // File selection handler
  const handleFileSelection = useCallback(async (selectedFile) => {
    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();

    if (!['csv', 'xlsx', 'xls'].includes(fileExtension)) {
      alert('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
      return;
    }

    setFile(selectedFile);
    setUploadState("parsing");

    try {
      let data;
      if (fileExtension === 'csv') {
        data = await parseCSVFile(selectedFile);
      } else {
        data = await parseExcelFile(selectedFile);
      }

      setParsedData(data);

      // Validate the parsed data (now async to support Google Maps API fallback)
      const validatedData = await validateBulkData(data, type);
      setValidationResults(validatedData);
      setUploadState("preview");

    } catch (error) {
      console.error('Error parsing file:', error);
      alert(`Error parsing file: ${error.message}`);
      setUploadState("idle");
    }
  }, [type]);

  // Drop handler
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelection(files[0]);
    }
  }, [handleFileSelection]);

  // File input handler
  const handleFileInput = useCallback((e) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFileSelection(files[0]);
    }
  }, [handleFileSelection]);

  // Single record test function for debugging
  const testSingleRecord = useCallback(async () => {
    const validRecords = validationResults.filter(item => item.isValid);
    
    if (validRecords.length === 0) {
      alert('No valid records to test. Please fix the errors and try again.');
      return;
    }

    console.log('🧪 Testing single record to diagnose database issue...');
    const testRecord = validRecords[0];
    
    try {
      const transformedData = transformBulkData(testRecord.data, type, corpId, corpName);
      console.log('🔍 Test record transformation successful:', transformedData);
      
      const testPayload = {
        ...transformedData,
        emailID: "",
      };
      
      console.log('🔍 Test payload being sent:', JSON.stringify(testPayload, null, 2));
      
      // Make the API call
      await new Promise((resolve, reject) => {
        sendRequest(
          {
            url: "/api/v1/Staff/AddEditStaff",
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: testPayload,
          },
          (response) => {
            console.log('🔍 Test record response:', response);
            
            // Handle numeric responses - matching individual registration behavior
            // The API returns various numeric codes for different success states
            if (typeof response === 'number') {
              console.log('✅ Test record response (numeric):', response);
              alert(`✅ Test record uploaded successfully! (Response code: ${response})`);
              resolve(response);
            } else if (response && response.Status === "000") {
              alert('✅ Test record uploaded successfully! Database issue resolved.');
              resolve(response);
            } else {
              console.error('❌ Test record failed:', response);
              alert(`❌ Test record failed: ${response?.DBErrorMessage || response?.Message || 'Unknown error'}`);
              reject(new Error(response?.DBErrorMessage || response?.Message || "Test failed"));
            }
          }
        );
      });
    } catch (error) {
      console.error('🔍 Test record error:', error);
      alert(`❌ Test record error: ${error.message}`);
    }
  }, [validationResults, type, corpId, corpName, sendRequest]);

  // Bulk upload handler
  const handleBulkUpload = useCallback(async () => {
    const validRecords = validationResults.filter(item => item.isValid);
    
    if (validRecords.length === 0) {
      alert('No valid records to upload. Please fix the errors and try again.');
      return;
    }

    setUploadState("uploading");
    setUploadProgress({ current: 0, total: validRecords.length, errors: [] });

    let successCount = 0;
    let failedCount = 0;
    const errors = [];

    for (let i = 0; i < validRecords.length; i++) {
      const record = validRecords[i];
      setUploadProgress(prev => ({ ...prev, current: i + 1 }));

      try {
        // Transform data to match API format
        const transformedData = transformBulkData(record.data, type, corpId, corpName);

        await new Promise((resolve, reject) => {
          console.log(`Uploading row ${record.rowIndex}:`, transformedData);
          
          sendRequest(
            {
              url: "/api/v1/Staff/AddEditStaff",
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: {
                ...transformedData,
                emailID: "",
              },
            },
            (response) => {
              console.log(`Response for row ${record.rowIndex}:`, response);
              
              // Handle numeric responses - matching individual registration behavior
              // The API returns various numeric codes (3, 16, 33, etc.)
              // Individual registration treats ALL numeric responses as success
              // because the API uses numbers for various success states
              if (typeof response === 'number') {
                console.log(`✅ Row ${record.rowIndex} uploaded successfully (numeric response: ${response})`);
                resolve(response);
              } 
              // Handle JSON responses with Status field
              else if (response && response.Status === "000") {
                resolve(response);
              } else {
                // Detailed database error logging
                if (response?.Status === "111") {
                  console.error(`❌ DATABASE ERROR for row ${record.rowIndex}:`, {
                    status: response.Status,
                    message: response.Message,
                    errorID: response.DBErrorMessageID,
                    severity: response.DBErrorSeverity,
                    state: response.DBErrorState,
                    line: response.DBErrorLine,
                    procedure: response.DBErrorProcedure,
                    dbMessage: response.DBErrorMessage,
                    sentData: transformedData
                  });
                }
                
                // More detailed error information
                const errorMsg = response?.DBErrorMessage || response?.Message || response?.Error || JSON.stringify(response) || "Upload failed";
                reject(new Error(errorMsg));
              }
            }
          );
        });
        successCount++;
      } catch (error) {
        console.error(`Error uploading row ${record.rowIndex}:`, error);
        failedCount++;
        errors.push({
          row: record.rowIndex,
          data: record.data,
          error: error.message || String(error)
        });
      }
    }

    setUploadResults({ success: successCount, failed: failedCount, errors });
    setUploadState("complete");
  }, [validationResults, type, corpId, corpName, sendRequest]);

  // Reset upload state
  const resetUpload = useCallback(() => {
    setUploadState("idle");
    setFile(null);
    setParsedData([]);
    setValidationResults([]);
    setUploadProgress({ current: 0, total: 0, errors: [] });
    setUploadResults({ success: 0, failed: 0, errors: [] });
    setDragActive(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Template download functions
  const getEmployeeTemplateData = () => {
    return [
      'First Name,Last Name,Department,Address,City,Area,Pickup Stop,Drop Stop,Mobile Number (9 digits),Country Code',
      'John,Doe,IT Department,"123 Tech Street, Westlands",Nairobi,Westlands,Westlands Mall,ABC Tech Park,712345678,254',
      'Sarah,Johnson,Marketing,"456 Business Ave, Karen",Nairobi,Karen,Karen Shopping Center,Corporate Plaza,723456789,254',
      'Michael,Smith,Finance,"789 Finance Road, Kilimani",Nairobi,Kilimani,Yaya Center,Finance Tower,734567890,254',
      'Priya,Patel,HR Department,"321 People Street, Parklands",Nairobi,Parklands,Parklands Mall,HR Building,745678901,254',
      'David,Wilson,Operations,"654 Ops Boulevard, Industrial Area",Nairobi,"Industrial Area",Industrial Mall,Operations Center,756789012,254'
    ];
  };

  const getStudentTemplateData = (schoolName = 'School Name') => {
    // NEW CSV FORMAT - matches the individual registration form structure
    // Morning Pickup: "From" (home location) → "To" (school name - auto-filled)
    // Afternoon Drop: "From" (school name - auto-filled) → "To" (home location)
    //
    // Columns: Student First Name, Student Last Name, Class, Parent First Name, Parent Last Name,
    //          Parent Mobile, Parent Email, Address, Area, City,
    //          Pickup From (user enters home), Pickup To (school - auto-filled),
    //          Drop From (school - auto-filled), Drop To (user enters home), Country Code
    //
    // NOTE: Names must contain only letters and spaces (no numbers)
    //       Mobile numbers should be 9 digits (without country code prefix)
    //       Coordinates are automatically looked up during validation
    return [
      'Student First Name,Student Last Name,Class,Parent First Name,Parent Last Name,Parent Mobile,Parent Email,Address,Area,City,Pickup From,Pickup To,Drop From,Drop To,Country Code',
      `John,Mwangi,Grade 8,Robert,Kamau,712345678,robert.kamau@email.com,"House 45, Lavington",Lavington,Nairobi,Yaya Centre,${schoolName},${schoolName},Lavington,254`,
      `Emma,Ochieng,Grade 5,Sarah,Otieno,723456789,sarah.otieno@email.com,"Apartment 12, Westlands",Westlands,Nairobi,Sarit Centre,${schoolName},${schoolName},Westlands,254`,
      `Ali,Hassan,Grade 10,Mohammed,Omar,734567890,mohammed.omar@email.com,"Villa 7, Karen",Karen,Nairobi,Karen Hub,${schoolName},${schoolName},Karen,254`,
      `Fatima,Wanjiku,Grade 3,Aisha,Njeri,745678901,aisha.njeri@email.com,"House 89, Kilimani",Kilimani,Nairobi,Prestige Plaza,${schoolName},${schoolName},Kilimani,254`,
      `Liam,Kipchoge,Grade 7,James,Korir,756789012,james.korir@email.com,"Unit 23, Kileleshwa",Kileleshwa,Nairobi,Valley Arcade,${schoolName},${schoolName},Kileleshwa,254`
    ];
  };

  const downloadTemplate = useCallback((registrationType) => {
    const templateData = registrationType === 'Student' 
      ? getStudentTemplateData(corpName || 'School Name') 
      : getEmployeeTemplateData();
    
    const csvContent = templateData.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${registrationType.toLowerCase()}_upload_template.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, []);

  return {
    // State
    fileInputRef,
    uploadState,
    file,
    parsedData,
    validationResults,
    uploadProgress,
    uploadResults,
    dragActive,
    isLoading,
    
    // Handlers
    handleDrag,
    handleDrop,
    handleFileInput,
    handleBulkUpload,
    testSingleRecord,
    resetUpload,
    downloadTemplate
  };
};
